import { useMemo } from 'react'
import { Box, IconButton, Autocomplete, TextField } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { useTranslation } from 'react-i18next'

import { kybStatusEnum } from '../../../../kyb/kyb.enums'
import { kycStatusEnum } from '../../../../kyc/kyc.enums'
import { CATEGORIES, customOperators } from '../../constantsV2'
import SelectInput from '../SelectInput'
import TextInput from '../TextInput'
import TagsInput from '../TagsInput'
import SelectListInput from '../SelectListInput'
import RangeNumberInput from '../RangeNumberInput'

const CustomRule = (props) => {
  const { t } = useTranslation(['common', 'messages'])
  const { rule, actions, schema, path, country } = props

  const handleExtraChange = (key, value) => {
    actions.onPropChange(key, value, path)
  }

  const handleOperatorChange = (value) => {
    handleExtraChange('operator', value)
    handleExtraChange('value', '')
  }

  const fieldData = schema.fieldMap?.[rule.attribute] || {}
  const operatorData = customOperators.find((op) => op.name === rule.operator) || {}
  const valueEditorType = operatorData.input

  // Special handling for kyc_status attributes
  const isKycStatusAttribute = rule.attribute === 'kyc_status'
  const isShareholdersKycStatusAttribute = rule.attribute === 'shareholders_kyc_status'
  const kycStatusOptions = kycStatusEnum.map((status) => ({
    label: t(`common:${status}`),
    value: status,
  }))

  const isLawsuitTypeEnum = ["CIVEL", "CRIMINAL", "TRIBUTARIA", "PREVIDENCIARIA", "TRABALHISTA", "ESPECIAL CIVEL", "ESPECIAL CRIMINAL", "ADMINISTRATIVA", "ELEITORAL", "FAZENDA", "ESPECIAL FAZENDA"]
  const isLawsuitTypeAttribute = ['has_lawsuit_court_type_person', 'has_lawsuit_court_type_business', 'has_lawsuit_court_type_shareholders'].includes(rule.attribute)
  const isLawsuitTypeOptions = isLawsuitTypeEnum.map((type) => ({
    label: type,
    value: type,
  }))

  const translatedCategoryOptions = useMemo(
    () =>
      CATEGORIES.map((category) => ({
        label: t(`common:${category.label}`, { defaultValue: category.label }),
        value: category.value,
      })).sort((a, b) => a.label.localeCompare(b.label)),
    [CATEGORIES, t],
  )

  const selectedCategory = translatedCategoryOptions.find((option) => option.value === rule.category) || null

  const translatedAttributeOptions = schema.fields
    .map((attribute) => ({
      label: t(`messages:riskmatrix.attribute.${country}.${attribute.name}`, { defaultValue: attribute.name }),
      value: attribute.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const selectedAttribute = translatedAttributeOptions.find((attr) => attr.value === rule.attribute) || null

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Box width={path.length < 2 ? '82%' : '92%'} display="flex" justifyContent="space-between" alignItems="center">
        {path.length < 2 && (
          <Box width="16%" maxWidth="16%">
            <Autocomplete
              size="small"
              value={selectedCategory}
              onChange={(event, value) => {
                handleExtraChange('category', value ? value.value : '')
              }}
              disablePortal
              options={translatedCategoryOptions}
              renderInput={(params) => (
                <TextField
                  variant="outlined"
                  placeholder={`${t('common:select')} ${t('common:category')}`}
                  {...params}
                />
              )}
            />
          </Box>
        )}

        <Box width={path.length < 2 ? '30%' : '35%'} maxWidth={path.length < 2 ? '30%' : '35%'}>
          <Autocomplete
            size="small"
            value={selectedAttribute}
            onChange={(event, value) => {
              handleExtraChange('attribute', value ? value.value : '')
            }}
            disablePortal
            options={translatedAttributeOptions}
            renderInput={(params) => (
              <TextField
                variant="outlined"
                placeholder={`${t('common:select')} ${t('common:attribute')}`}
                {...params}
              />
            )}
          />
        </Box>

        <Box width={path.length < 2 ? '20%' : '25%'} maxWidth={path.length < 2 ? '20%' : '25%'}>
          <SelectInput
            value={rule.operator || ''}
            onChange={handleOperatorChange}
            options={(fieldData.operators || []).map((operator) => ({
              label: t(`messages:riskmatrix.operator.${operator}`),
              value: operator,
            }))}
            emptyLabel={`${t('common:select')} ${t('common:operator')}`}
            disabled={!rule.attribute}
            required
          />
        </Box>

        <Box width={path.length < 2 ? '25%' : '30%'} maxWidth={path.length < 2 ? '25%' : '30%'}>
          {/* Special handling for kyc_status with equals operator */}
          {isKycStatusAttribute && valueEditorType === 'string' && (
            <SelectInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              options={kycStatusOptions}
              placeholder={`${t('common:select')} ${t('common:value')}`}
              required
            />
          )}
          {/* Special handling for kyc_status with in_list operator */}
          {isKycStatusAttribute && valueEditorType === 'list' && (
            <TagsInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              options={kycStatusEnum}
              placeholder={`${t('common:select')} ${t('common:value')}`}
              required
            />
          )}
          {/* Special handling for shareholders_kyc_status with any_not_includes (string input) */}
          {isShareholdersKycStatusAttribute && valueEditorType === 'string' && (
            <SelectInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              options={kycStatusOptions}
              placeholder={`${t('common:select')} ${t('common:value')}`}
              required
            />
          )}
          {/* Special handling for shareholders_kyc_status with includes_any_value (list input) */}
          {isShareholdersKycStatusAttribute && valueEditorType === 'list' && (
            <TagsInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              options={kycStatusEnum}
              placeholder={`${t('common:select')} ${t('common:value')}`}
              required
            />
          )}
          {/* Special handling for lawsuit court typess with equals operator */}
          {isLawsuitTypeAttribute && valueEditorType === 'string' && (
            <SelectInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              options={isLawsuitTypeOptions}
              placeholder={`${t('common:select')} ${t('common:value')}`}
              required
            />
          )}
          {/* Special handling for lawsuit court typess with in_list operator */}
          {isLawsuitTypeAttribute && valueEditorType === 'list' && (
            <TagsInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              options={isLawsuitTypeEnum}
              placeholder={`${t('common:select')} ${t('common:value')}`}
              required
            />
          )}
                    {/* Default string input for non-kyc_status attributes */}
          {!isKycStatusAttribute && !isShareholdersKycStatusAttribute && !isLawsuitTypeAttribute && (!valueEditorType || valueEditorType === 'string') && (
            <TextInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              placeholder={`${t('common:insert')} ${t('common:value')}`}
            />
          )}
                    {/* Default list input for non-kyc_status attributes */}
          {!isKycStatusAttribute && !isShareholdersKycStatusAttribute && !isLawsuitTypeAttribute && valueEditorType === 'list' && (
            <TagsInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              placeholder={`${t('common:insert')} ${t('common:value')}`}
              required
            />
          )}
          {valueEditorType === 'custom_list' && (
            <SelectListInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              placeholder={`${t('common:insert')} ${t('common:value')}`}
              type="neutral"
              context="riskMatrix"
              required
            />
          )}
          {valueEditorType === 'custom_list_composed' && (
            <SelectListInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              placeholder={`${t('common:insert')} ${t('common:value')}`}
              type="neutral"
              context="riskMatrix"
              isComposed
              required
            />
          )}
          {valueEditorType === 'number' && (
            <TextInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              type="number"
              placeholder={`${t('common:insert')} ${t('common:value')}`}
              required
            />
          )}
          {valueEditorType === 'number_range' && (
            <RangeNumberInput value={rule.value} onChange={(value) => handleExtraChange('value', value)} required />
          )}
          {valueEditorType === 'boolean' && (
            <SelectInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              options={[
                { label: 'True', value: true },
                { label: 'False', value: false },
              ]}
              placeholder={`${t('common:insert')} ${t('common:value')}`}
              required
            />
          )}
          {valueEditorType === 'list_sizes_descriptions' && (
            <SelectInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              options={['Micro Empresa', 'Empresa de Pequeno Porte', 'Demais', 'Não informado'].map((size) => ({
                label: size,
                value: size,
              }))}
              placeholder={`${t('common:insert')} ${t('common:value')}`}
              required
            />
          )}
          {valueEditorType === 'list_sanctions_source' && (
            <TagsInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              options={[
                'interpol',
                'fbi',
                'ofac',
                'uk',
                'eu',
                'unsc',
                'cepim',
                'ceis',
                'MTE',
                'cnep',
                'United Nations Security Council',
                'Ibama',
                'Securities and Exchange Commission',
                'Court of Auditors of the State of São Paulo',
                'National Council of Justice',
                'Federal Court of Auditors',
                'BSM Supervision',
                'Bank Hindered Suppliers',
                'ca-sanctions',
              ]}
              placeholder={`${t('common:select')} ${t('common:value')}`}
              required
            />
          )}
          {valueEditorType === 'list_rejected_checks_source' && (
            <TagsInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              options={['SIN FONDOS', 'DEFECTOS FORMALES']}
              placeholder={`${t('common:select')} ${t('common:value')}`}
              required
            />
          )}

          {valueEditorType === 'list_debtors_checks_source' && (
            <TagsInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              options={['1', '2', '3', '4', '5']}
              placeholder={`${t('common:select')} ${t('common:value')}`}
              required
            />
          )}
          {valueEditorType === 'list_status' && !isShareholdersKycStatusAttribute && (
            <SelectInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              options={kybStatusEnum.map((status) => ({
                label: t(`common:${status}`),
                value: status,
              }))}
              placeholder={`${t('common:insert')} ${t('common:value')}`}
              required
            />
          )}
          {/* Special handling for shareholders_kyc_status with list_status (all_are_status) */}
          {valueEditorType === 'list_status' && isShareholdersKycStatusAttribute && (
            <SelectInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              options={kycStatusOptions}
              placeholder={`${t('common:select')} ${t('common:value')}`}
              required
            />
          )}
          {valueEditorType === 'list_kyc_status' && (
            <SelectInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              options={kycStatusEnum.map((status) => ({
                label: t(`common:${status}`),
                value: status,
              }))}
              placeholder={`${t('common:insert')} ${t('common:value')}`}
              required
            />
          )}
          {valueEditorType === 'list_employment_status' && (
            <SelectInput
              value={rule.value}
              onChange={(value) => handleExtraChange('value', value)}
              options={[
                { label: 'Jubilado', value: 'retired' },
                { label: 'Empleado', value: 'employee' },
                { label: 'Monotributista', value: 'monotax' },
                { label: 'Autonomo', value: 'selfEmployed' },
                { label: 'Desempleado', value: 'unemployed' },
              ]}
              placeholder={`${t('common:insert')} ${t('common:value')}`}
              required
            />
          )}
        </Box>
      </Box>
      <Box width={path.length < 2 ? '15%' : '5%'} display="flex" gap="8px" alignItems="center">
        {path.length < 2 && (
          <TextInput
            value={rule.score}
            onChange={(value) => handleExtraChange('score', value === '' ? '' : Number(value))}
            type="number"
            placeholder={t('common:score')}
            required
          />
        )}
        <IconButton onClick={() => actions.onRuleRemove(path)} color="error">
          <DeleteIcon />
        </IconButton>
      </Box>
    </Box>
  )
}

export default CustomRule
